import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'
import { unique } from 'remeda'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { Changelog } from '@/types/changelog'
import { getLightUsersByIds } from '@/services/user/user.query'
import { getChangelogsByEntityIdQuery } from '@/services/graphql'
import { changeLogsAdapter } from '@/services/adapters'
import { getAmplifyClient } from '@/services/amplifyClient'
import { isSystemChangeLogActor } from '@/services/changelogValue'
const client = getAmplifyClient()

type AdaptedChangelog = NonNullable<
  Awaited<ReturnType<typeof changeLogsAdapter>>[number]
>

export const useChangeLogsByEntityId = ({ entityID }: { entityID: string }) => {
  const t = useTranslations()
  const [adaptedLogs, setAdaptedLogs] = useState<
    AdaptedChangelog[] | undefined
  >(undefined)
  const [isAdapting, setIsAdapting] = useState(false)

  const query = useQuery({
    queryKey: ['changelogs', entityID],
    queryFn: async () => await getChangelogsByEntityId({ id: entityID }),
    enabled: !!entityID,
  })

  useEffect(() => {
    let cancelled = false

    if (!query.data) {
      setAdaptedLogs(undefined)
      setIsAdapting(false)
      return
    }

    const safeT = (
      key: string,
      params?: Record<string, string | number | null | undefined>,
    ) => {
      if (!params) return t(key)
      const sanitized: Record<string, string | number | Date> = {}
      for (const [paramKey, value] of Object.entries(params)) {
        sanitized[paramKey] = value ?? ''
      }
      return t(key, sanitized)
    }

    setIsAdapting(true)

    void changeLogsAdapter(query.data, safeT)
      .then((nextLogs) => {
        if (cancelled) return
        setAdaptedLogs(
          nextLogs.filter((log): log is AdaptedChangelog => log !== null),
        )
        setIsAdapting(false)
      })
      .catch((error) => {
        if (cancelled) return
        console.error(error)
        setAdaptedLogs(undefined)
        setIsAdapting(false)
      })

    return () => {
      cancelled = true
    }
  }, [query.data, t])

  return {
    ...query,
    data: adaptedLogs,
    isFetching: query.isFetching || isAdapting,
  }
}

export const getChangelogsByEntityId = async ({
  id,
}: {
  id: string
}): Promise<Changelog[]> => {
  try {
    const {
      data: { changeLogByEntityID },
    } = (await client.graphql({
      query: getChangelogsByEntityIdQuery,
      variables: {
        entityID: id,
      },
    })) as GraphQLResult<{ changeLogByEntityID: { items: Changelog[] } }>

    const uniqueUserIds = unique(
      changeLogByEntityID.items
        .map((changelog) => changelog.changedBy)
        .filter(
          (changedBy): changedBy is string =>
            Boolean(changedBy) && !isSystemChangeLogActor(changedBy),
        ),
    )

    const usersData =
      uniqueUserIds.length > 0 ? await getLightUsersByIds(uniqueUserIds) : []
    const usersMap = new Map(usersData.map((user) => [user.id, user]))

    return changeLogByEntityID.items.map((changelog) => {
      const user = isSystemChangeLogActor(changelog.changedBy)
        ? {
            id: changelog.changedBy,
            name: 'System',
            email: 'system',
          }
        : usersMap.get(changelog.changedBy) || {
            id: changelog.changedBy,
            name: 'Unknown',
            email: 'unknown',
          }

      return { ...changelog, user }
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

/** Raw changelogs hook for restore workflows (unadapted items) */
export const useRawChangeLogsByEntityId = ({ id }: { id: string }) => {
  return useQuery({
    queryKey: ['raw-changelogs', id],
    queryFn: async () => await getChangelogsByEntityId({ id }),
    enabled: !!id,
  })
}
